import { useState } from "react";
import type { AgentWorkbenchPatch } from "../../../../shared/types";
import { ArtifactDocumentReview } from "../workbench/ArtifactDocumentReview";
import { artifactWorkspaceViewModel } from "../../lib/artifact-workspace";
import { StrategyDetail } from "../brand/StrategyDetail";
import {
  type StrategyPatchRequest,
  useApplyBrandStrategyWorkbenchPatch,
  useBrandStrategyWorkbench,
  useConfirmStrategy,
  useProposeBrandStrategyWorkbenchPatch,
  useStrategy,
} from "../../hooks/useStrategy";

type StrategyWorkspaceSurfaceProps = {
  brandId: string;
  brandName: string;
  workspaceHref?: string | null;
  locationLabel?: string;
};

export function StrategyWorkspaceSurface({
  brandId,
  brandName,
  locationLabel = "Brand docs / Strategy",
}: StrategyWorkspaceSurfaceProps) {
  const { data: strategyData, isLoading, isError, error, refetch } = useStrategy(brandId);
  const {
    data: workbenchData,
    isLoading: isWorkbenchLoading,
    isError: isWorkbenchError,
    refetch: refetchWorkbench,
  } = useBrandStrategyWorkbench(brandId);
  const proposePatch = useProposeBrandStrategyWorkbenchPatch(brandId);
  const applyPatch = useApplyBrandStrategyWorkbenchPatch(brandId);
  const confirmStrategy = useConfirmStrategy(brandId);
  const [proposal, setProposal] = useState<AgentWorkbenchPatch | null>(null);
  const [lastRequest, setLastRequest] = useState<StrategyPatchRequest | null>(null);
  const [proposalError, setProposalError] = useState<string | null>(null);
  const strategy = strategyData?.data ?? null;
  const statusLabel = workbenchData?.data?.status
    ? workbenchData.data.status
        .split("_")
        .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
        .join(" ")
    : "Draft";
  const workspaceModel = workbenchData?.data
    ? artifactWorkspaceViewModel(workbenchData.data, {
      provenanceLabel: "strategy-artifact.md",
      proposal,
    })
    : null;
  const confirmError = confirmStrategy.isError ? "Could not confirm strategy. Try again." : null;

  function acceptProposal(nextProposal: AgentWorkbenchPatch) {
    setProposal(nextProposal);
  }

  if (isWorkbenchLoading) {
    return (
      <div className="flex min-h-[120px] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span className="rounded-full bg-slate-50 px-2.5 py-1 ring-1 ring-slate-200">{locationLabel}</span>
        <span className="rounded-full bg-slate-50 px-2.5 py-1 ring-1 ring-slate-200">{brandName}</span>
        <span className="rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 font-semibold text-indigo-700">
          {workspaceModel?.statusLabel ?? statusLabel}
        </span>
        {workspaceModel?.updatedAtLabel ? (
          <span className="rounded-full bg-slate-50 px-2.5 py-1 ring-1 ring-slate-200">
            Updated {workspaceModel.updatedAtLabel}
          </span>
        ) : null}
      </div>

      {isWorkbenchError
        ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-950 shadow-sm">
            <p className="font-medium">Could not load the strategy artifact right now.</p>
            <p className="mt-2">
              The canonical strategy draft remains available below, and you can retry the artifact
              review without leaving this workspace.
            </p>
            <button
              type="button"
              onClick={() => void refetchWorkbench()}
              className="mt-3 rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-semibold text-amber-900 hover:border-amber-400"
            >
              Retry strategy artifact
            </button>
          </div>
        )
        : workbenchData?.data
        ? (
          <ArtifactDocumentReview
            manifest={workbenchData.data}
            proposal={proposal}
            proposalError={proposalError}
            proposalPending={proposePatch.isPending}
            applyPending={applyPatch.isPending}
            provenanceLabel="strategy-artifact.md"
            onCancelProposal={() => {
              setProposal(null);
              setProposalError(null);
            }}
            onRejectProposal={() => {
              setProposal(null);
              setProposalError(null);
            }}
            onAskAgainProposal={async () => {
              if (!lastRequest) return;
              try {
                setProposalError(null);
                const result = await proposePatch.mutateAsync(lastRequest);
                acceptProposal(result.data);
              } catch (nextError) {
                setProposal(null);
                setProposalError(nextError instanceof Error ? nextError.message : "Could not generate a new proposal.");
              }
            }}
            onApplyProposal={async () => {
              if (!proposal) return;
              try {
                setProposalError(null);
                await applyPatch.mutateAsync(proposal);
                setProposal(null);
              } catch (nextError) {
                setProposalError(nextError instanceof Error ? nextError.message : "Could not apply this proposal.");
              }
            }}
            onRequestProposal={async (request) => {
              setLastRequest(request);
              try {
                setProposalError(null);
                const result = await proposePatch.mutateAsync(request);
                acceptProposal(result.data);
                return true;
              } catch (nextError) {
                setProposal(null);
                setProposalError(nextError instanceof Error ? nextError.message : "Could not generate a proposal.");
                return false;
              }
            }}
          />
        )
        : (
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm">
            Strategy artifact data is not available yet. Refresh this page or retry after the
            artifact loads.
          </div>
        )}

      <details className="rounded-[20px] border border-dashed border-slate-300 bg-slate-50/80 p-4 shadow-sm md:p-5">
        <summary className="flex cursor-pointer list-none flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-data text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
              Secondary tools
            </p>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">
              Keep the campaign-facing strategy surface available, but secondary to the canonical document.
            </p>
          </div>
          <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
            Expand
          </span>
        </summary>

        <details className="mt-4 group rounded-2xl border border-slate-200 bg-white shadow-sm">
          <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">Canonical strategy</p>
              <p className="mt-1 text-sm text-slate-600">
                This remains the execution surface for campaigns and confirmation while the
                markdown-first review flow becomes primary.
              </p>
            </div>
            <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
              Strategy draft
            </span>
          </summary>

          <div className="border-t border-slate-200 px-4 py-4">
            {isError
              ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  <p>
                    Could not load strategy. {error instanceof Error ? error.message : "Try again in a moment."}
                  </p>
                  <button
                    type="button"
                    onClick={() => void refetch()}
                    className="mt-2 text-sm font-medium text-red-900 underline"
                  >
                    Retry
                  </button>
                </div>
              )
              : isLoading
              ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  Loading canonical strategy...
                </div>
              )
              : !strategy
              ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="max-w-2xl">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                        Canonical strategy
                      </p>
                      <h3 className="mt-2 text-2xl font-semibold text-slate-950">No saved strategy yet</h3>
                      <p className="mt-2 text-sm text-slate-700">
                        {brandName} does not currently have a valid saved brand strategy. Use the strategy thread to create one, then return here to confirm the result.
                      </p>
                    </div>
                    <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700">
                      Absent
                    </span>
                  </div>
                </div>
              )
              : (
                <StrategyDetail
                  strategy={strategy}
                  brandId={brandId}
                  onConfirm={() => confirmStrategy.mutate()}
                  confirming={confirmStrategy.isPending}
                  confirmError={confirmError}
                />
              )}
          </div>
        </details>
      </details>
    </div>
  );
}
